/**
 * quickBillSuggest — type-ahead for the invoice-style Quick Bill form.
 *
 * Read-only. Everything a merchant has typed before comes back as they type:
 *   buyer  → customers of this store, then suppliers
 *   item   → catalogue products, then items typed on earlier bills
 *   HSN    → the synced HSN master, then the built-in HSN list
 *
 * Rates are returned in the form's convention (before GST unless the form's
 * "Rates include GST" is on), converted from the store's stored convention.
 */
import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Product, Supplier } from '@/db/models/Inventory';
import { HsnMaster } from '@/db/models/System';
import { HSN_DATABASE } from '@/utils/hsnDatabase';
import type { ProductForBilling } from './billingEngine';
import { recentManualItems } from './manualItem';
import { toEnteredSellingPrice } from './priceMode';
import { toBillingProduct } from './toBillingProduct';

const like = (q: string) => `%${Q.sanitizeLikeString(q)}%`;

export interface BuyerSuggestion {
  kind: 'customer' | 'supplier';
  id: string;
  name: string;
  gstin: string;
  phone: string;
  address: string;
}

export async function suggestBuyers(storeId: string, query: string): Promise<BuyerSuggestion[]> {
  const q = query.trim();
  if (!q) return [];
  const [customers, suppliers] = await Promise.all([
    database.get<Customer>('customers').query(
      Q.where('store_id', storeId),
      Q.where('is_deleted', false),
      Q.or(Q.where('name', Q.like(like(q))), Q.where('phone', Q.like(like(q))), Q.where('gstin', Q.like(like(q)))),
      Q.take(6),
    ).fetch(),
    database.get<Supplier>('suppliers').query(
      Q.where('is_deleted', false),
      Q.or(Q.where('name', Q.like(like(q))), Q.where('phone', Q.like(like(q))), Q.where('gstin', Q.like(like(q)))),
      Q.take(4),
    ).fetch(),
  ]);
  const out: BuyerSuggestion[] = customers.map((c) => ({
    kind: 'customer', id: c.id, name: c.name ?? '', gstin: c.gstin ?? '', phone: c.phone ?? '', address: c.address ?? '',
  }));
  // A supplier already billed as a customer shows once, as the customer.
  const seenGstin = new Set(out.map((c) => c.gstin.toUpperCase()).filter(Boolean));
  for (const s of suppliers) {
    if (s.gstin && seenGstin.has(s.gstin.toUpperCase())) continue;
    out.push({ kind: 'supplier', id: s.id, name: s.name ?? '', gstin: s.gstin ?? '', phone: s.phone ?? '', address: s.address ?? '' });
  }
  return out;
}

export interface ItemSuggestion {
  kind: 'catalogue' | 'typed';
  /** Set for catalogue products — the line then bills that product. */
  product: ProductForBilling | null;
  name: string;
  hsn: string;
  gstRate: number;
  cessRate: number;
  unit: string;
  /** In the form's convention. */
  rate: number;
  /** Catalogue products that track stock. */
  stock: number | null;
}

export async function suggestItems(
  storeId: string,
  query: string,
  ratesIncludeGst: boolean,
  storeIsInclusive: boolean,
): Promise<ItemSuggestion[]> {
  const q = query.trim();
  if (!q) return [];
  const [products, typed] = await Promise.all([
    database.get<Product>('products').query(
      Q.where('store_id', storeId),
      Q.where('is_deleted', false),
      Q.where('is_active', true),
      Q.or(Q.where('name', Q.like(like(q))), Q.where('sku', Q.like(like(q))), Q.where('barcode', q)),
      Q.take(6),
    ).fetch(),
    recentManualItems(storeId, q, 4),
  ]);
  const entered = (stored: number, gst: number, cess: number) =>
    toEnteredSellingPrice(stored, gst, cess, ratesIncludeGst, storeIsInclusive);

  const out: ItemSuggestion[] = products.map((p) => ({
    kind: 'catalogue',
    product: toBillingProduct(p),
    name: p.name,
    hsn: p.hsnCode ?? '',
    gstRate: p.gstRate,
    cessRate: p.cessRate ?? 0,
    unit: p.unit || 'NOS',
    rate: entered(p.sellingPrice, p.gstRate, p.cessRate ?? 0),
    stock: p.trackInventory ? p.currentStock : null,
  }));
  const names = new Set(out.map((o) => o.name.trim().toLowerCase()));
  for (const t of typed) {
    if (names.has(t.name.trim().toLowerCase())) continue;
    out.push({
      kind: 'typed', product: null, name: t.name, hsn: t.hsn ?? '', gstRate: t.gstRate, cessRate: 0,
      unit: t.unit || 'NOS', rate: entered(t.rate, t.gstRate, 0), stock: null,
    });
  }
  return out;
}

export interface HsnSuggestion { code: string; description: string; gstRate: number; cessRate: number }

/** By code prefix or by words in the description; the synced master first. */
export async function suggestHsn(query: string, limit = 8): Promise<HsnSuggestion[]> {
  const q = query.trim();
  if (!q) return [];
  const isCode = /^\d+$/.test(q);
  let master: HsnMaster[] = [];
  try {
    master = await database.get<HsnMaster>('hsn_master').query(
      isCode ? Q.where('code', Q.like(`${Q.sanitizeLikeString(q)}%`)) : Q.or(
        Q.where('description', Q.like(like(q))),
        Q.where('keywords', Q.like(like(q))),
      ),
      Q.take(limit),
    ).fetch();
  } catch {
    master = []; // a till that has not pulled the master yet still gets the built-in list
  }
  const out: HsnSuggestion[] = master.map((h) => ({ code: h.code, description: h.description, gstRate: h.gstRate, cessRate: h.cessRate ?? 0 }));
  const seen = new Set(out.map((h) => h.code));
  const lower = q.toLowerCase();
  for (const h of HSN_DATABASE) {
    if (out.length >= limit) break;
    if (seen.has(h.code)) continue;
    const hit = isCode
      ? h.code.startsWith(q)
      : h.description.toLowerCase().includes(lower) || h.keywords.some((k) => k.toLowerCase().includes(lower));
    if (hit) { out.push({ code: h.code, description: h.description, gstRate: h.gstRate, cessRate: h.cessRate }); seen.add(h.code); }
  }
  return out;
}
